import React, { useEffect } from 'react';
import './userFileUploader.module.scss';
import { UserFileShowView, UserFileToken } from '../../../types/uploadedFiles';
import { UserFileUploader } from './UserFileUploader';
import { action, observable, makeObservable } from 'mobx';
import { RemoteResourceV3 } from '../../../../commonv3/api/RemoteResourceV3';
import { UserFilesApi } from '../../../apis/userFiles';
import { translateErrorMessage } from '../../../../commonv3/api/protocol';
import { observer, useLocalStore } from 'mobx-react';
import { UploadedFileList } from './UploadedFileLIst';
import { FailedFileList, UploadingFileList } from './UploadingFileList';
import { ByteUtils } from '../../../../commonv3/utils/bytes';

export type UploadRequest = {
  id: number;
  file: File;
  progress: number;
};

export type FailedUploadRequest = {
  id: number;
  file: File;
  message: string;
};

const MB_IN_BYTES = 1048576;
const MAX_FILE_SIZE_IN_BYTES = 50 * MB_IN_BYTES;

class UserFileUploadInputState {
  nextId = 0;
  fileTokens: UserFileToken[] = [];
  uploadRequests: UploadRequest[] = [];
  failedRequests: FailedUploadRequest[] = [];
  files: RemoteResourceV3<UserFileShowView[]> = RemoteResourceV3.of([]);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onChange!: (tokens: UserFileToken[]) => void;

  constructor() {
    makeObservable(this, {
      nextId: observable,
      fileTokens: observable,
      uploadRequests: observable,
      failedRequests: observable,
      files: observable,
      onChange: observable,
      upload: action.bound,
      updateRequest: action.bound,
      deleteFile: action.bound,
      addFile: action.bound,
    });
  }

  init(fileTokens: UserFileToken[], onChange: (files: UserFileToken[]) => void) {
    this.fileTokens = fileTokens;
    this.onChange = onChange;
  }

  async upload(files: File[]) {
    this.failedRequests = [];
    await Promise.all(
      files.map(async (file) => {
        const request = { id: this.nextId++, file, progress: 0 };
        this.uploadRequests.push(request);
        try {
          if (request.file.size >= MAX_FILE_SIZE_IN_BYTES) {
            throw new Error(
              `アップロードできるファイルサイズは最大${ByteUtils.formatBytes(MAX_FILE_SIZE_IN_BYTES)}までです。`
            );
          }
          const uploaded = await UserFilesApi.create(file, (event) => {
            this.updateRequest(request.id, (r) => ({ ...r, progress: event.loaded / event.total }));
          });
          this.uploadRequests = this.uploadRequests.filter((r) => r.id !== request.id);
          this.addFile(uploaded);
        } catch (e) {
          this.uploadRequests = this.uploadRequests.filter((r) => r.id !== request.id);
          this.failedRequests.push({ ...request, message: translateErrorMessage(e) });
        }
      })
    );
  }

  updateRequest(id: number, updater: (r: UploadRequest) => UploadRequest) {
    this.uploadRequests = this.uploadRequests.map((request) => {
      if (request.id == id) {
        return updater(request);
      }
      return request;
    });
  }
  deleteFile(token: UserFileToken) {
    this.files = this.files.map((list) => list.filter((f) => f.token !== token));
    this.notifyChange();
  }
  addFile(file: UserFileShowView) {
    this.files = this.files.map((list) => list.concat([file]));
    this.notifyChange();
  }
  notifyChange() {
    if (this.onChange) {
      this.onChange(this.files.getOrElse([]).map((f) => f.token));
    }
  }
}

type Props = {
  id?: string;
  value: UserFileToken[];
  onChange: (nextValue: UserFileToken[]) => void;
};
export const UserFileUploadInput = observer(({ id, value, onChange }: Props) => {
  const { state } = useLocalStore(() => ({
    state: new UserFileUploadInputState(),
  }));
  useEffect(() => {
    state.init(value, onChange);
  }, [value]);
  return (
    <div>
      <UserFileUploader id={id} onUploadRequest={(files: File[]) => state.upload(files)} />
      <UploadingFileList requests={state.uploadRequests} />
      <FailedFileList requests={state.failedRequests} />
      <UploadedFileList files={state.files.getOrElse([])} onRequestDelete={state.deleteFile} />
    </div>
  );
});
